// The page's flood guard as Solid reads it (#6753): ONE `FloodGuard` per Fleet page, made by
// `FleetMachines` and handed to every card through {@link FloodGuardContext}. Every reader
// here reads `tick()` first, so a view re-runs when the guard moves (a change queued, a save
// landing, the lock's countdown). No guard in context means NO controls: a card mounted
// outside the page shows none, and never makes a per-card guard of its own, which would let
// a second six changes through (`./flood-guard.ts`).

import { type Accessor, createContext, createSignal, onCleanup, useContext } from 'solid-js';
import { BROWSER_TIMERS, createFloodGuard, type FloodGuard, type FloodTimers } from './flood-guard';

export type PageGuard = {
  readonly guard: FloodGuard;
  /** Bumped whenever the guard moves; read it to subscribe. */
  readonly tick: Accessor<number>;
};

export const FloodGuardContext = createContext<PageGuard | null>(null);

/** The Fleet page's one guard, disposed with the page: a waiting change is then never sent. */
export function createPageGuard(timers: FloodTimers = BROWSER_TIMERS): PageGuard {
  const guard = createFloodGuard(timers);
  const [tick, setTick] = createSignal(0);
  const off = guard.subscribe(() => setTick((n) => n + 1));
  onCleanup(() => {
    off();
    guard.dispose();
  });
  return { guard, tick };
}

/** The page's guard, or null outside a Fleet page (then the card shows no controls). */
export function usePageGuard(): PageGuard | null {
  return useContext(FloodGuardContext) ?? null;
}

/** Seconds until the page unlocks, 0 when unlocked; tracked. */
export function lockedSeconds(page: PageGuard): number {
  page.tick();
  return page.guard.lockedSeconds();
}

/** Whether the control's change is in flight; tracked. */
export function isSaving(page: PageGuard, key: string): boolean {
  page.tick();
  return page.guard.saving(key);
}

/** The value the control's waiting change asks for, undefined when none waits; tracked. */
export function wantedValue(page: PageGuard, key: string): unknown {
  page.tick();
  return page.guard.wanted(key);
}
